import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

const WIDTH = 1120;
const HEIGHT = 410;
const MARGIN = { top: 26, right: 22, bottom: 48, left: 66 };

export const CHIP_DESIGNERS = [
    { id: 'Nvidia', label: 'Nvidia', color: '#153f78' },
    { id: 'Google', label: 'Google', color: '#11998e' },
    { id: 'Amazon', label: 'Amazon', color: '#d4853f' },
    { id: 'AMD', label: 'AMD', color: '#6d5eb5' },
    { id: 'Huawei', label: 'Huawei', color: '#a47a34' },
    { id: 'Cambricon', label: 'Cambricon', color: '#7c8996' },
];

export const formatH100e = (value, digits = 1) => {
    const numeric = Number(value || 0);
    if (numeric >= 1e6) return `${(numeric / 1e6).toFixed(digits)}M`;
    if (numeric >= 1e3) return `${(numeric / 1e3).toFixed(digits)}K`;
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(numeric);
};

const formatQuarter = (date, isZh) => {
    const parsed = new Date(`${date}T00:00:00Z`);
    const quarter = Math.floor(parsed.getUTCMonth() / 3) + 1;
    return isZh
        ? `${parsed.getUTCFullYear()}Q${quarter}`
        : `Q${quarter} ${parsed.getUTCFullYear()}`;
};

export const aggregateH100eByQuarter = (records = [], cumulative = false) => {
    const periods = new Map();
    records.forEach((row) => {
        if (!row?.periodEnd || !Number.isFinite(row.median)) return;
        const point = periods.get(row.periodEnd) || {
            date: row.periodEnd,
            total: 0,
            low: 0,
            high: 0,
            incomplete: false,
            components: {},
        };
        point.total += row.median;
        point.low += Number(row.low || 0);
        point.high += Number(row.high || 0);
        point.incomplete = point.incomplete || Boolean(row.incomplete);
        point.components[row.designer] = (point.components[row.designer] || 0) + row.median;
        periods.set(row.periodEnd, point);
    });

    const quarterly = [...periods.values()].sort((left, right) => left.date.localeCompare(right.date));
    if (!cumulative) return quarterly;

    const runningComponents = {};
    let runningTotal = 0;
    let runningLow = 0;
    let runningHigh = 0;
    return quarterly.map((point) => {
        CHIP_DESIGNERS.forEach(({ id }) => {
            runningComponents[id] = (runningComponents[id] || 0) + Number(point.components[id] || 0);
        });
        runningTotal += point.total;
        runningLow += point.low;
        runningHigh += point.high;
        return {
            ...point,
            total: runningTotal,
            low: runningLow,
            high: runningHigh,
            components: { ...runningComponents },
        };
    });
};

const niceMaximum = (value) => {
    const exponent = 10 ** Math.floor(Math.log10(Math.max(value, 1)));
    const normalized = value / exponent;
    const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return factor * exponent;
};

const ChipComputeCapacityChart = ({ dataset, isZh = false }) => {
    const [view, setView] = useState('quarterly');
    const [activeIndex, setActiveIndex] = useState(null);
    const scrollRef = useRef(null);
    const patternId = useId().replaceAll(':', '');
    const quarterly = useMemo(() => aggregateH100eByQuarter(dataset?.records || []), [dataset?.records]);
    const cumulative = useMemo(() => aggregateH100eByQuarter(dataset?.records || [], true), [dataset?.records]);
    const data = view === 'cumulative' ? cumulative : quarterly;

    useEffect(() => {
        const scrollToLatest = () => {
            const element = scrollRef.current;
            if (element && window.innerWidth <= 760) element.scrollLeft = element.scrollWidth;
        };
        scrollToLatest();
        window.addEventListener('resize', scrollToLatest);
        return () => window.removeEventListener('resize', scrollToLatest);
    }, [data.length, view]);

    if (!data.length) return <div className="ai-dev-chart-empty">Epoch chip-sales data unavailable</div>;

    const latestComplete = [...quarterly].reverse().find((point) => !point.incomplete) || quarterly[quarterly.length - 1];
    const latestCompleteCumulative = cumulative[quarterly.indexOf(latestComplete)];
    const plotWidth = WIDTH - MARGIN.left - MARGIN.right;
    const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
    const maxValue = niceMaximum(Math.max(...data.map((point) => point.high), 1));
    const y = (value) => MARGIN.top + plotHeight - (value / maxValue) * plotHeight;
    const barSlot = plotWidth / data.length;
    const barWidth = Math.min(42, Math.max(18, barSlot * 0.68));
    const ticks = Array.from({ length: 5 }, (_, index) => (maxValue / 4) * index);
    const tickIndexes = [...new Set([0, Math.round((data.length - 1) * 0.2), Math.round((data.length - 1) * 0.4), Math.round((data.length - 1) * 0.6), Math.round((data.length - 1) * 0.8), data.length - 1])];
    const activePoint = activeIndex == null ? null : data[activeIndex];
    const activeSegments = activePoint ? CHIP_DESIGNERS
        .map((designer) => ({ ...designer, value: Number(activePoint.components[designer.id] || 0) }))
        .filter(({ value }) => value > 0)
        .sort((left, right) => right.value - left.value) : [];

    return (
        <div className="ai-dev-openrouter-chart ai-dev-chip-compute">
            <div className="ai-dev-chip-compute__summary">
                <span>{isZh ? '最新完整季度' : 'Latest complete quarter'}<strong>{formatH100e(latestComplete.total)}</strong><small>{formatQuarter(latestComplete.date, isZh)}</small></span>
                <span>{isZh ? '累计交付算力' : 'Cumulative delivered compute'}<strong>{formatH100e(latestCompleteCumulative.total)}</strong><small>{isZh ? '截至最新完整季度' : 'through latest complete quarter'}</small></span>
                <span>{isZh ? '季度估算区间' : 'Quarter estimate range'}<strong>{formatH100e(latestComplete.low)}–{formatH100e(latestComplete.high)}</strong><small>{isZh ? '5–95 分位数' : '5th–95th percentile'}</small></span>
            </div>
            <div className="ai-dev-openrouter-chart__toolbar">
                <span>{isZh ? '按芯片设计商堆叠' : 'Stacked by chip designer'}</span>
                <div className="ai-dev-openrouter-segmented" role="group" aria-label={isZh ? 'H100 等效视图' : 'H100-equivalent view'}>
                    {[
                        ['quarterly', isZh ? '季度新增' : 'Quarterly'],
                        ['cumulative', isZh ? '累计交付' : 'Cumulative'],
                    ].map(([value, label]) => (
                        <button
                            className={view === value ? 'is-active' : ''}
                            type="button"
                            key={value}
                            aria-pressed={view === value}
                            onClick={() => {
                                setView(value);
                                setActiveIndex(null);
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="ai-dev-openrouter-chart__scroll" ref={scrollRef}>
                <div className="ai-dev-openrouter-chart__plot">
                    <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={isZh ? `AI 芯片${view === 'cumulative' ? '累计交付' : '季度新增'}算力 H100 等效堆叠图` : `AI chip ${view} delivered compute in H100 equivalents`}>
                        <defs>
                            <pattern id={patternId} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                <rect width="3" height="7" fill="rgba(255,255,255,0.58)" />
                            </pattern>
                        </defs>
                        {ticks.map((tick) => (
                            <g key={tick}>
                                <line className="ai-dev-openrouter-chart__grid" x1={MARGIN.left} x2={WIDTH - MARGIN.right} y1={y(tick)} y2={y(tick)} />
                                <text className="ai-dev-openrouter-chart__tick" x={MARGIN.left - 12} y={y(tick) + 4} textAnchor="end">{formatH100e(tick, 1)}</text>
                            </g>
                        ))}
                        {data.map((point, index) => {
                            const x = MARGIN.left + barSlot * index + (barSlot - barWidth) / 2;
                            let stackedValue = 0;
                            return (
                                <g
                                    className="ai-dev-openrouter-chart__bar"
                                    key={point.date}
                                    role="button"
                                    tabIndex="0"
                                    aria-label={`${formatQuarter(point.date, isZh)}, ${formatH100e(point.total)} H100e${point.incomplete ? `, ${isZh ? '未完整' : 'incomplete'}` : ''}`}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onMouseLeave={() => setActiveIndex(null)}
                                    onFocus={() => setActiveIndex(index)}
                                    onBlur={() => setActiveIndex(null)}
                                    onClick={() => setActiveIndex(index)}
                                >
                                    {CHIP_DESIGNERS.map((designer) => {
                                        const value = Number(point.components[designer.id] || 0);
                                        if (!value) return null;
                                        stackedValue += value;
                                        return (
                                            <rect
                                                key={designer.id}
                                                x={x}
                                                y={y(stackedValue)}
                                                width={barWidth}
                                                height={Math.max(0, (value / maxValue) * plotHeight)}
                                                fill={designer.color}
                                            />
                                        );
                                    })}
                                    {point.incomplete && <rect x={x} y={y(point.total)} width={barWidth} height={Math.max(0, (point.total / maxValue) * plotHeight)} fill={`url(#${patternId})`} />}
                                    <line className="ai-dev-chip-compute__interval" x1={x + barWidth / 2} x2={x + barWidth / 2} y1={y(Math.min(point.high, maxValue))} y2={y(point.low)} />
                                    <line className="ai-dev-chip-compute__interval" x1={x + barWidth / 2 - 4} x2={x + barWidth / 2 + 4} y1={y(Math.min(point.high, maxValue))} y2={y(Math.min(point.high, maxValue))} />
                                    <line className="ai-dev-chip-compute__interval" x1={x + barWidth / 2 - 4} x2={x + barWidth / 2 + 4} y1={y(point.low)} y2={y(point.low)} />
                                    <rect className="ai-dev-openrouter-chart__hit" x={x - 3} y={MARGIN.top} width={barWidth + 6} height={plotHeight} />
                                </g>
                            );
                        })}
                        {tickIndexes.map((index) => {
                            const x = MARGIN.left + barSlot * index + barSlot / 2;
                            return <text className="ai-dev-openrouter-chart__tick" key={data[index].date} x={x} y={HEIGHT - 16} textAnchor="middle">{formatQuarter(data[index].date, isZh)}</text>;
                        })}
                    </svg>
                    {activePoint && (
                        <div
                            className="ai-dev-openrouter-chart__tooltip ai-dev-chip-compute__tooltip"
                            style={{ left: `${Math.min(88, Math.max(12, (activeIndex / Math.max(1, data.length - 1)) * 100))}%` }}
                        >
                            <time>{formatQuarter(activePoint.date, isZh)}{activePoint.incomplete ? ` · ${isZh ? '未完整' : 'incomplete'}` : ''}</time>
                            <strong>{formatH100e(activePoint.total)} H100e</strong>
                            <small>{isZh ? '估算区间' : 'Estimate range'} {formatH100e(activePoint.low)}–{formatH100e(activePoint.high)}</small>
                            {activeSegments.map((segment) => (
                                <span key={segment.id}>
                                    <i style={{ background: segment.color }} />
                                    <em>{segment.label}</em>
                                    <b>{formatH100e(segment.value)}</b>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="ai-dev-openrouter-legend" aria-label={isZh ? '芯片设计商图例' : 'Chip designer legend'}>
                {CHIP_DESIGNERS.map((designer) => <span key={designer.id}><i style={{ background: designer.color }} />{designer.label}</span>)}
            </div>
            <p className="ai-dev-openrouter-chart__note">
                {isZh
                    ? 'H100e 按峰值 8-bit 算力折算；误差线为 5–95 分位数，斜纹表示季度数据尚未完整。累计口径不扣除芯片退役。'
                    : 'H100e normalizes peak 8-bit compute; whiskers show 5th–95th percentiles and hatching marks incomplete quarters. Cumulative totals do not subtract retirements.'}
            </p>
        </div>
    );
};

export default ChipComputeCapacityChart;
